import { useState, useRef, useEffect } from "react";
import { Home, Plus, Menu, X, ArrowLeft, ArrowRight, Lock, Camera, Upload, MessageCircle, Bell, Circle, CheckCircle2, BookOpen } from "lucide-react";
import { DOCS, QUESTIONS, UI } from "./flowData";

const Bubble = ({ mine, children, testid }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
    <div data-testid={testid} className={`max-w-[88%] px-4 py-3 text-sm sm:text-base rounded-lg ${mine ? "bg-[#FFF1DA] text-[#14183E] font-medium" : "bg-white border border-[#E7E5DF] text-[#1E1D4C]"}`}>{children}</div>
  </div>
);

const Drawer = ({ title, sub, onClose, testid, children, rtl }) => (
  <aside data-testid={testid} className={`fixed top-0 bottom-0 z-40 w-full sm:w-[400px] bg-white border-[#E7E5DF] flex flex-col ${rtl ? "left-0 border-r" : "right-0 border-l"}`}>
    <div className="px-5 h-16 border-b border-[#E7E5DF] flex items-center justify-between shrink-0">
      <div><div className="font-semibold text-[#181E4B]">{title}</div>{sub && <div className="text-xs text-[#5E6282]">{sub}</div>}</div>
      <button data-testid={`${testid}-close`} onClick={onClose}><X size={20} /></button>
    </div>
    <div className="p-5 overflow-y-auto">{children}</div>
  </aside>
);

const Card = ({ title, sub, onExplain, L, children }) => (
  <div data-testid="chat-question-card" className="w-full max-w-xl border border-[#E7E5DF] bg-white rounded-lg overflow-hidden">
    <div className="bg-[#181E4B] text-white px-5 py-4 flex items-start justify-between gap-3">
      <div><div className="font-semibold text-lg">{title}</div>{sub && <div className="text-sm text-white/70 mt-0.5">{sub}</div>}</div>
      <button data-testid="explain-step-btn" onClick={onExplain} className="shrink-0 text-xs border border-white/60 px-3 py-1.5 rounded-md hover:bg-white/10 flex items-center gap-1.5"><BookOpen size={13} />{L(UI.explain)}</button>
    </div>
    <div className="p-5 space-y-3">{children}</div>
  </div>
);

function Upload4({ L, next }) {
  const items = ["Passports (current and old)", "Flight reservation", "Employment letter", "Proof of accommodation", "Bank statements"];
  return (<>
    <div className="divide-y divide-[#E7E5DF] border border-[#E7E5DF] rounded-md">
      {items.map((i) => <div key={i} className="flex items-center justify-between px-4 py-3 text-sm"><span className="flex items-center gap-2"><Circle size={12} className="text-[#5E6282]" />{i}</span><button className="border border-[#181E4B] text-[#181E4B] px-3 py-1 rounded-md text-xs font-medium flex items-center gap-1"><Upload size={12} />Upload</button></div>)}
    </div>
    <p className="text-xs text-[#5E6282] flex gap-2"><Lock size={14} className="shrink-0" />Your files are encrypted in transit and at rest. Only our qualified team can open them.</p>
    <button data-testid="upload-continue-btn" onClick={() => next("Documents reviewed")} className="w-full bg-[#181E4B] text-white py-3 rounded-md font-medium flex items-center justify-center gap-2">{L(UI.cont)}<ArrowRight size={16} /></button>
  </>);
}

function Done() {
  return (
    <div data-testid="end-state" className="w-full max-w-xl border border-[#E7E5DF] bg-white rounded-lg">
      <div className="h-1 bg-[#F1A501]" />
      <div className="p-6 space-y-5">
        <h3 className="font-serif font-bold text-2xl text-[#181E4B]">Your file is ready</h3>
        <ul className="space-y-2 text-sm">
          {["Applicant details: complete", "VIDEX answers: prepared", "Forms and letters: drafted", "Mission: German Embassy, Islamabad"].map((t) => <li key={t} className="flex gap-2 items-center"><CheckCircle2 size={16} className="text-[#15803D]" />{t}</li>)}
        </ul>
        <div className="bg-[#FFF1DA] p-4 rounded-md">
          <div className="font-semibold text-[#14183E]">Next: we book your appointment</div>
          <p className="text-sm text-[#2C304E] mt-1">We watch the official appointment system for you. You get an SMS and WhatsApp message as soon as a slot is confirmed.</p>
        </div>
      </div>
    </div>
  );
}

export default function FlowChat({ ur, setUr }) {
  const L = (x) => (ur && x.ur ? x.ur : x.en);
  const [answers, setAnswers] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [menu, setMenu] = useState(false);
  const end = useRef(null);
  const q = QUESTIONS[answers.length];
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [answers.length]);
  const next = (label) => setAnswers((a) => [...a, label]);
  const total = QUESTIONS.length + 4;
  const Arrow = ur ? ArrowRight : ArrowLeft;

  return (
    <div dir={ur ? "rtl" : "ltr"} className={`h-screen flex bg-[#FBFAF7] ${ur ? "font-urdu" : ""}`} data-testid="chat-root">
      <aside data-testid="chat-sidebar-nav" className={`${menu ? "fixed inset-y-0 z-50 flex" : "hidden"} lg:flex w-64 shrink-0 bg-[#181E4B] text-white flex-col`}>
        <div className="h-16 px-5 flex items-center justify-between font-serif font-bold text-xl">HolaVisto<button className="lg:hidden" onClick={() => setMenu(false)}><X size={18} /></button></div>
        <nav className="px-3 space-y-1 text-sm">
          <a href="/" className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-white/10"><Home size={16} />Home</a>
          <button data-testid="sidebar-new-app-btn" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-white/10"><Plus size={16} />New application</button>
        </nav>
        <div className="px-5 mt-6 text-xs tracking-wider text-white/60">IN PROGRESS</div>
        <div className="px-3 mt-2"><div className="bg-white/10 border-s-2 border-[#F1A501] px-3 py-2.5 rounded-md text-sm"><div className="font-medium">Germany Tourist, Islamabad</div><div className="text-xs text-white/70">Step {Math.min(answers.length + 2, total)} of {total}</div></div></div>
        <div className="mt-auto p-3 text-sm space-y-1">
          <button className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/10 rounded-md"><MessageCircle size={16} />Chat on WhatsApp</button>
          <div className="flex items-center gap-3 px-3 py-3 border-t border-white/15"><span className="h-8 w-8 bg-[#F1A501] text-[#14183E] font-semibold flex items-center justify-center rounded-sm">A</span><span>Ahmed Khan</span></div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header data-testid="chat-top-header" className="h-16 bg-white border-b border-[#E7E5DF] px-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMenu(true)} data-testid="mobile-menu-btn"><Menu size={20} /></button>
            <span className="h-9 w-9 bg-[#181E4B] text-white text-sm font-semibold flex items-center justify-center rounded-sm">H</span>
            <div><div className="text-sm font-semibold text-[#181E4B] flex items-center gap-2">Holavisto Assistant<span className="h-2 w-2 bg-[#15803D] rounded-full" /></div><div className="text-xs text-[#5E6282]">Germany Tourist visa, Islamabad</div></div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="lang-toggle-ur" onClick={() => setUr(!ur)} className="text-sm border border-[#E7E5DF] px-3 py-1.5 rounded-md">{ur ? "English" : "اردو"}</button>
            <Bell size={18} className="text-[#5E6282]" />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 py-6 space-y-4 pb-28">
            <div className="bg-white border border-[#E7E5DF] rounded-lg p-5 space-y-3 text-[#1E1D4C]" data-testid="assistant-welcome-bubble">
              <p className="font-semibold">{ur ? "السلام علیکم! خوش آمدید" : "Welcome to Holavisto."}</p>
              <p>{L(UI.welcome1)} {L(UI.welcome2)}</p>
              <button data-testid="view-doc-list-link" onClick={() => setDrawer("docs")} className="text-[#029BC5] underline font-medium">{L(UI.link)}</button>
              <p>{L(UI.welcome3)}</p>
            </div>
            {answers.map((a, i) => (
              <div key={i} className="space-y-4">
                <Bubble>{QUESTIONS[i].title && L(QUESTIONS[i].title)}</Bubble>
                <Bubble mine testid="user-reply-bubble">{a}</Bubble>
              </div>
            ))}
            {q ? (
              <div className="space-y-3">
                <div><div className="flex justify-between text-xs text-[#5E6282] mb-1"><span>Step {answers.length + 2} of {total}</span><span>{q.id === "who" || q.id === "job" ? "Your situation" : "Documents"}</span></div><div className="h-1 bg-[#E7E5DF]"><div className="h-1 bg-[#F1A501]" style={{ width: `${((answers.length + 2) / total) * 100}%` }} /></div></div>
                {answers.length > 0 && <button data-testid="chat-back-btn" onClick={() => setAnswers(answers.slice(0, -1))} className="text-sm border border-[#E7E5DF] bg-white px-3 py-1.5 rounded-md inline-flex items-center gap-1.5"><Arrow size={14} />{L(UI.back)}</button>}
                <Card title={L(q.title)} L={L} onExplain={() => setDrawer("explain")}>
                  {q.kind === "choice" && q.options.map((o) => (
                    <button key={o.v} data-testid={`option-${o.v}`} onClick={() => next(L(o.t))} className="w-full text-start border border-[#E7E5DF] rounded-md px-4 py-3.5 hover:border-[#181E4B] hover:bg-[#FFF1DA]/40 transition-colors">
                      <div className="font-semibold text-[#1E1D4C]">{L(o.t)}</div><div className="text-sm text-[#5E6282]">{L(o.s)}</div>
                    </button>
                  ))}
                  {q.kind === "passport" && <>
                    <div className="bg-[#FFF1DA] border border-[#F1A501]/40 px-4 py-3 text-sm rounded-md">Just the photo page (with your picture and details) is enough. Photos and PDFs both work.</div>
                    <button data-testid="chat-scan-passport" onClick={() => next("Passport scanned")} className="w-full flex items-center gap-3 border border-[#181E4B] px-4 py-3.5 rounded-md text-start"><Camera size={20} /><span><span className="block font-semibold">Scan passport</span><span className="block text-sm text-[#5E6282]">Upload a photo or PDF for instant data extraction</span></span></button>
                    <button onClick={() => next("Typing by hand")} className="block mx-auto text-sm text-[#5E6282] underline">Prefer typing? Enter the details by hand</button>
                  </>}
                  {q.kind === "upload" && <Upload4 L={L} next={next} />}
                </Card>
              </div>
            ) : <Done />}
            <div ref={end} />
          </div>
        </main>
      </div>

      {drawer === "docs" && <Drawer rtl={ur} testid="doc-list-drawer" title="Documents for the German Embassy, Islamabad" sub="Tourist visa (Schengen, Type C)" onClose={() => setDrawer(null)}>
        <ol className="divide-y divide-[#E7E5DF]">{DOCS.map(([n, s], i) => <li key={n} className="py-3 flex gap-3"><span className="text-xs text-[#5E6282] w-5 pt-0.5">{i + 1}</span><span><span className="block font-semibold text-[#1E1D4C] text-sm">{n}</span><span className="block text-xs text-[#5E6282]">{s}</span></span></li>)}</ol>
        <p className="text-xs text-[#5E6282] mt-4">Source: German Missions in Pakistan. Shown for information. You do not need to do anything here.</p>
      </Drawer>}
      {drawer === "explain" && q && <Drawer rtl={ur} testid="explain-drawer" title={L(UI.explain)} sub="Holavisto explains only. Your answers never change from here." onClose={() => setDrawer(null)}>
        <p className="text-[#2C304E] leading-relaxed">{L(q.explain)}</p>
        <button className="mt-6 w-full border border-[#181E4B] text-[#181E4B] py-2.5 rounded-md text-sm font-medium flex items-center justify-center gap-2"><MessageCircle size={16} />Still stuck? Ask a specialist on WhatsApp</button>
      </Drawer>}
    </div>
  );
}
